import React, {
	useEffect,
	useRef,
	useState,
} from 'react';

import {
	Button,
	Col,
	Dropdown,
	Form,
	Input,
	message,
	Modal,
	Pagination,
	Popconfirm,
	Row,
	Spin,
	Table,
} from 'antd';

import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	EditOutlined,
	EyeInvisibleOutlined,
	EyeOutlined,
	InfoCircleOutlined,
	PlayCircleOutlined,
} from '@ant-design/icons';

import { debounce } from 'lodash';
import moment from 'moment';

import { IoSearchOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';

import { api } from '../../../api/api';

import SuccessIcon from '../../../assets/successicon.svg';

import styles from './pendingModifyNric.module.scss';

const PendingModifyNric = () => {

	/*
	 * Logged-in user information
	 */
	const userDetails = JSON.parse(
		localStorage.getItem('user-onboard-data')
	);

	/*
	 * Same Admin identification used in
	 * existing onboarding / deactivation screens.
	 */
	const isAdmin =
		userDetails?.isAdmin === 'Y';

	const [form] = Form.useForm();

	const [data, setData] =
		useState([]);

	const [loading, setLoading] =
		useState(false);

	const [searchText, setSearchText] =
		useState('');

	const [rejectModal, setRejectModal] =
		useState(null);

	const [visibleNric, setVisibleNric] =
		useState({});

	const [processingId, setProcessingId] =
		useState(null);

	const [reject, setReject] =
		useState({
			loading: false,
			success: null,
			error: null,
		});

	const [requestStatus, setRequestStatus] =
		useState({
			loading: false,
			success: null,
			error: null,
		});

	const [tableParams, setTableParams] =
		useState({
			pagination: {
				current: 1,
				pageSize: 10,
				total: 0,
			},
		});


	/*
	 * =========================================================
	 * NRIC VISIBILITY
	 * =========================================================
	 *
	 * Old and New NRIC are masked.
	 *
	 * When user clicks eye icon:
	 * show for 10 seconds and hide automatically.
	 */
	const toggleNric = (key) => {

		setVisibleNric((prev) => {

			const isNowVisible =
				!prev[key];

			if (isNowVisible) {

				setTimeout(() => {

					setVisibleNric(
						(current) => ({
							...current,
							[key]: false,
						})
					);

				}, 10000);
			}

			return {
				...prev,
				[key]: isNowVisible,
			};
		});
	};


	/*
	 * =========================================================
	 * FETCH PENDING NRIC MODIFICATION LIST
	 * =========================================================
	 *
	 * Admin:
	 * /getAdminAllModifyNricPendingList
	 *
	 * NH User:
	 * /getUserAllModifyNricPendingList
	 *
	 * Normal user's NH can contain multiple NH names:
	 *
	 * Lions,RenCi
	 *
	 * Backend service already splits them.
	 */
	const fetchPendingList = async () => {
	setLoading(true);

	try {
		const {
			current,
			pageSize,
		} = tableParams.pagination;

		const response = await api.get({
			path:
				userDetails?.isAdmin === 'Y'
					? '/getAdminAllModifyNricPendingList'
					: `/getUserAllModifyNricPendingList?nhName=${userDetails?.nhName}`,

			params: {
				page: current,
				size: pageSize,
				searchText,
			},

			service: 'useronboarding',
		});

		if (response.status === 200) {
			const pendingList =
				response.data?.data || [];

			const totalData =
				response.data?.pagination?.totalData;

			setData(pendingList);

			setTableParams((prev) => ({
				...prev,
				pagination: {
					...prev.pagination,
					total:
						totalData ||
						response.data?.data?.length,
				},
			}));
		}

	} catch (error) {
		console.error(
			'Pending NRIC List Error:',
			error
		);

		message.error(
			'Unable to retrieve pending NRIC modification requests.'
		);

	} finally {
		setLoading(false);
	}
};


	/*
	 * =========================================================
	 * LOAD LIST
	 * =========================================================
	 */
	useEffect(() => {

		fetchPendingList();

		// eslint-disable-next-line
	}, [
		tableParams.pagination.current,
		tableParams.pagination.pageSize,
		searchText,
	]);


	/*
	 * =========================================================
	 * PAGINATION
	 * =========================================================
	 */
	const handleTableChange = (
		pagination
	) => {

		setTableParams(
			(prev) => ({
				...prev,

				pagination: {
					...prev.pagination,

					...pagination,
				},
			})
		);
	};


	/*
	 * =========================================================
	 * SEARCH
	 * =========================================================
	 *
	 * Search when:
	 *
	 * 3+ characters
	 *
	 * OR
	 *
	 * empty text to reset.
	 */
	const handleSearch = (e) => {

		const value =
			e.target.value || '';

		const trimmedValue =
			value.trim();

		if (
			trimmedValue.length >= 3 ||
			trimmedValue.length === 0
		) {

			setSearchText(
				trimmedValue
			);

			setTableParams(
				(prev) => ({
					...prev,

					pagination: {
						...prev.pagination,

						current: 1,
					},
				})
			);
		}
	};


	const debouncedSearch =
		useRef(
			debounce(
				handleSearch,
				300
			)
		).current;


	useEffect(() => {

		return () => {

			debouncedSearch.cancel();
		};

	}, [debouncedSearch]);


	/*
	 * =========================================================
	 * ADMIN - PROCESS REQUEST
	 * =========================================================
	 *
	 * Only ADMIN can execute this.
	 *
	 * This should call the endpoint that invokes your
	 * EXISTING NRIC processing method.
	 *
	 * That backend method should:
	 *
	 * 1. Read pending request
	 * 2. Validate Old NRIC
	 * 3. Update AD
	 * 4. Update SSO
	 * 5. Update Clinical if required
	 * 6. Set final status
	 *
	 * Change only the path below if your existing
	 * endpoint has a different URL.
	 */
	const processRequest = async (row) => {

		if (!isAdmin) {

			message.error(
				'Only Admin can process NRIC modification requests.'
			);

			return;
		}


		const status =
			String(
				row?.status || ''
			)
				.trim()
				.toUpperCase();


		if (status !== 'P') {

			message.error(
				'Only pending requests can be processed.'
			);

			return;
		}


		setProcessingId(
			row.id
		);

		setRequestStatus({
			loading: true,
			success: null,
			error: null,
		});


		try {

			/*
			 * IMPORTANT:
			 *
			 * This is the endpoint that should invoke
			 * your existing processing method.
			 *
			 * If your actual endpoint name is different,
			 * change ONLY this path.
			 */
			await api.post({

				path:
					'/process-modify-nric',

				service:
					'useronboarding',

				formdata: {
					id: row.id,

					modifiedBy:
						userDetails?.userId,
				},
			});


			setRequestStatus({
				loading: false,

				success:
					'NRIC modification request processed successfully.',

				error: null,
			});


			message.success(
				'NRIC modification request processed successfully!'
			);


			/*
			 * Refresh pending list.
			 *
			 * Successfully processed request should
			 * no longer have P status and therefore
			 * disappear from this screen.
			 */
			await fetchPendingList();

		} catch (err) {

			console.error(
				'Process NRIC Error:',
				err
			);


			const errorMessage =
				err?.response
					?.data
					?.message ||

				err?.response
					?.data ||

				err?.message ||

				'Unable to process NRIC modification request.';


			setRequestStatus({
				loading: false,
				success: null,
				error:
					errorMessage,
			});


			message.error(
				errorMessage
			);

		} finally {

			setProcessingId(
				null
			);
		}
	};


	/*
	 * =========================================================
	 * ADMIN - REJECT REQUEST
	 * =========================================================
	 */
	const rejectRequest = async (
		values
	) => {

		if (
			!isAdmin ||
			!rejectModal
		) {
			return;
		}


		const formDataObject = {

			id:
				rejectModal.id,

			status:
				'R',

			modifiedBy:
				userDetails?.userId,

			remarks:
				values.remarks?.trim(),
		};


		setReject({
			loading: true,
			success: null,
			error: null,
		});


		try {

			await api.post({

				/*
				 * Change this URL only if your
				 * backend uses another endpoint.
				 */
				path:
					'/reject-modify-nric',

				service:
					'useronboarding',

				formdata:
					formDataObject,
			});


			setReject({
				loading: false,

				success:
					'NRIC modification request rejected successfully.',

				error: null,
			});


			message.success(
				'NRIC modification request rejected successfully!'
			);


			setRejectModal(
				null
			);

			form.resetFields();

			await fetchPendingList();

		} catch (err) {

			console.error(
				'Reject NRIC Error:',
				err
			);


			const errorMessage =
				err?.response
					?.data
					?.message ||

				err?.response
					?.data ||

				err?.message ||

				'Unable to reject NRIC modification request.';


			setReject({
				loading: false,
				success: null,
				error:
					errorMessage,
			});


			message.error(
				errorMessage
			);

		}
	};


	/*
	 * =========================================================
	 * ACTION MENU
	 * =========================================================
	 *
	 * NH USER:
	 *
	 * P:
	 * - Modify Request
	 *
	 *
	 * ADMIN:
	 *
	 * P:
	 * - Modify Request
	 * - Process Request
	 * - Reject
	 */
	const renderRowActions = (
		row
	) => {

		const status =
			String(
				row?.status || ''
			)
				.trim()
				.toUpperCase();


		/*
		 * Pending screen should normally
		 * contain only P.
		 *
		 * Still guard it here.
		 */
		if (status !== 'P') {
			return null;
		}


		const items = [];


		/*
		 * =====================================================
		 * MODIFY
		 * =====================================================
		 *
		 * Both Admin and NH users can modify
		 * a PENDING request.
		 */
		items.push({

			key: 'modify',

			label: (

				<Link
					to={
						isAdmin

							? `/admin/ModifyUserDetails/modify-nric?id=${row.id}`

							: `/ModifyUserDetails/modify-nric?id=${row.id}`
					}
				>

					<div
						className={
							styles.dropdownItem
						}
					>

						<InfoCircleOutlined
							className={
								styles.infoIcon
							}
						/>

						<span>
							Modify Request
						</span>

					</div>

				</Link>
			),
		});


		/*
		 * =====================================================
		 * ADMIN ONLY ACTIONS
		 * =====================================================
		 */
		if (isAdmin) {

			items.push(

				/*
				 * PROCESS REQUEST
				 */
				{
					key:
						'process',

					label: (

						<Popconfirm
							title="Process NRIC Modification"
							description="Are you sure you want to process this NRIC modification request?"
							okText="Process"
							cancelText="Cancel"

							onConfirm={() =>
								processRequest(
									row
								)
							}
						>

							<div
								className={
									styles.dropdownItem
								}
							>

								<PlayCircleOutlined
									className={
										styles.approveIcon
									}
								/>

								<span>
									Process Request
								</span>

							</div>

						</Popconfirm>
					),
				},

				/*
				 * REJECT
				 */
				{
					key:
						'reject',

					label: (

						<div
							className={
								styles.dropdownItem
							}

							onClick={() =>
								setRejectModal(
									row
								)
							}
						>

							<CloseCircleOutlined
								className={
									styles.rejectIcon
								}
							/>

							<span>
								Reject
							</span>

						</div>
					),
				}
			);
		}


		return (

			<div
				className={
					styles.actionTrigger
				}
			>

				<Dropdown
					menu={{
						items,
					}}

					placement="bottomRight"

					trigger={[
						'click',
					]}
				>

					<EditOutlined />

				</Dropdown>

			</div>
		);
	};


	/*
	 * =========================================================
	 * STATUS
	 * =========================================================
	 */
	const getStatusTag = (
		status
	) => {

		switch (
			String(
				status || ''
			).toUpperCase()
		) {

			case 'A':

				return (
					<span
						className={
							styles.badgeApproved
						}
					>
						Approved
					</span>
				);


			case 'R':

				return (
					<span
						className={
							styles.badgeRejected
						}
					>
						Rejected
					</span>
				);


			case 'P':

				return (
					<span
						className={
							styles.badgePending
						}
					>
						Pending
					</span>
				);


			case 'C':

				return (
					<span
						className={
							styles.badgeApproved
						}
					>
						Completed
					</span>
				);


			case 'F':

				return (
					<span
						className={
							styles.badgeFailed
						}
					>
						Failed
					</span>
				);


			default:

				return (
					<span
						className={
							styles.badgeUnknown
						}
					>
						Unknown
					</span>
				);
		}
	};


	/*
	 * =========================================================
	 * NRIC DISPLAY
	 * =========================================================
	 */
	const renderNric = (
		value,
		record,
		type
	) => {

		if (!value) {
			return '-';
		}


		const key =
			`${record.id}-${type}`;


		const isVisible =
			visibleNric[key];


		/*
		 * Example:
		 *
		 * S1234567D
		 *
		 * becomes:
		 *
		 * *****567D
		 */
		const masked =
			value.replace(
				/.(?=.{4})/g,
				'*'
			);


		return (

			<div
				style={{
					display:
						'flex',

					alignItems:
						'center',

					gap:
						8,
				}}
			>

				<span>
					{
						isVisible
							? value
							: masked
					}
				</span>


				{
					isVisible
						? (

							<EyeInvisibleOutlined
								className={
									styles.nricEyeIcon
								}

								onClick={() =>
									toggleNric(
										key
									)
								}
							/>

						)
						: (

							<EyeOutlined
								className={
									styles.nricEyeIcon
								}

								onClick={() =>
									toggleNric(
										key
									)
								}
							/>

						)
				}

			</div>
		);
	};


	/*
	 * =========================================================
	 * TABLE COLUMNS
	 * =========================================================
	 */
	const columns = [

		{
			title:
				'Status',

			dataIndex:
				'status',

			key:
				'status',

			width:
				110,

			render:
				getStatusTag,
		},


		{
			title:
				'Nursing Home',

			dataIndex:
				'nhName',

			key:
				'nhName',

			minWidth:
				200,
		},


		{
			title:
				'Username',

			dataIndex:
				'userName',

			key:
				'userName',

			minWidth:
				180,
		},
        {
            title: 'New Username',
            dataIndex: 'newUserName',
            key: 'newUserName',
            minWidth: 180,
            render: (value) => value || '-',
        },

		{
			title:
				'Full Name',

			dataIndex:
				'fullName',

			key:
				'fullName',

			minWidth:
				250,
		},


		{
			title:
				'Old NRIC / FIN',

			dataIndex:
				'oldNric',

			key:
				'oldNric',

			minWidth:
				190,

			render: (
				value,
				record
			) =>
				renderNric(
					value,
					record,
					'old'
				),
		},


		{
			title:
				'New NRIC / FIN',

			dataIndex:
				'newNric',

			key:
				'newNric',

			minWidth:
				190,

			render: (
				value,
				record
			) =>
				renderNric(
					value,
					record,
					'new'
				),
		},
        {
            title: 'Completion Email List',
            dataIndex: 'completionEmailList',
            key: 'completionEmailList',
            minWidth: 280,
            render: (value) => {
                if (!value) {
                    return '-';
                }

                return String(value)
                    .split(',')
                    .map((email) => (
                        <div key={email.trim()}>
                            {email.trim()}
                        </div>
                    ));
            },
        },

		{
			title:
				'Submitted By',

			dataIndex:
				'createdBy',

			key:
				'createdBy',

			minWidth:
				180,
		},


		{
			title:
				'Submitted Date',

			dataIndex:
				'createdDate',

			key:
				'createdDate',

			minWidth:
				180,

			render:
				(value) =>

					value
						? moment(
								value
							).format(
								'DD-MMM-YYYY HH:mm'
							)

						: '-',
		},


		{
			title:
				'Remarks',

			dataIndex:
				'remarks',

			key:
				'remarks',

			minWidth:
				280,

			render:
				(value) =>
					value || '-',
		},


		/*
		 * Action column.
		 *
		 * Pending records only.
		 */
		{
			title:
				'Action',

			key:
				'action',

			fixed:
				'right',

			width:
				100,

			render:
				(_, row) =>
					renderRowActions(
						row
					),
		},
	];


	const modalStyles = {

		header: {

			borderBottom:
				'1px solid #E0E1E2',

			paddingBottom:
				5,
		},
	};


	return (

		<div
			className={
				styles['wrapper']
			}
		>

			{/* HEADER */}
			<div
				className={
					styles[
						'top-section'
					]
				}
			>

				<div
					className={
						styles[
							'top-section-left'
						]
					}
				>

					<div
						className={
							styles['left']
						}
					>

						<p
							className={
								styles['title']
							}
						>
							Pending NRIC Modification
						</p>


						<div
							className={
								styles['totals']
							}
						>

							<img
								className={
									styles[
										'success-icons'
									]
								}

								src={
									SuccessIcon
								}

								alt="Success Icon"
							/>


							<p
								className={
									styles[
										'totals'
									]
								}
							>
								Total Requests :{' '}
								{
									tableParams
										.pagination
										.total
								}
							</p>

						</div>

					</div>


					<div
						className={
							styles['right']
						}
					>

						<Input
							prefix={
								<IoSearchOutline />
							}

							className={
								styles[
									'search-input'
								]
							}

							placeholder="Search by full name, username or new username (min 3 characters)"

							onPressEnter={
								handleSearch
							}

							onChange={
								debouncedSearch
							}

							allowClear
						/>

					</div>

				</div>


				{
					requestStatus.loading && (

						<div
							className={
								styles[
									'saving-request'
								]
							}
						>

							<Spin />

							<b>
								Processing Request
							</b>

						</div>
					)
				}

			</div>


			{/* TABLE */}
			<div
				className={
					styles.horizontalFixed
				}
			>

				<div
					className={
						styles.verticalScroll
					}
				>

					<Table
						className={
							styles.table
						}

						rowKey={
							(record) =>
								record.id
						}

						dataSource={
							data
						}

						columns={
							columns
						}

						loading={
							loading
						}

						pagination={
							false
						}

						tableLayout="auto"

						locale={{
							emptyText: (

								<div
									style={{
										padding:
											'40px 0',

										textAlign:
											'center',
									}}
								>
									No pending NRIC modification requests found
								</div>
							),
						}}

						rowClassName={(
							_,
							index
						) =>
							index % 2 === 0
								? styles.evenRow
								: styles.oddRow
						}

						scroll={{
							x:
								'max-content',

							y:
								450,
						}}
					/>

				</div>

			</div>


			{/* PAGINATION */}
			<div
				className={
					styles.paginationWrapper
				}
			>

				<Pagination
					current={
						tableParams
							.pagination
							.current
					}

					pageSize={
						tableParams
							.pagination
							.pageSize
					}

					total={
						tableParams
							.pagination
							.total
					}

					showSizeChanger

					showQuickJumper

					hideOnSinglePage

					onChange={(
						page,
						pageSize
					) =>

						handleTableChange({
							current:
								page,

							pageSize,
						})
					}
				/>

			</div>


			{/* REJECT MODAL */}
			{
				!!rejectModal && (

					<Modal
						title={
							<p>
								Reject NRIC Modification Request
							</p>
						}

						open={
							!!rejectModal
						}

						onCancel={() => {

							setRejectModal(
								null
							);

							form.resetFields();
						}}

						destroyOnClose

						centered

						footer={
							false
						}

						styles={
							modalStyles
						}

						width={
							500
						}
					>

						<Form
							id="modify-nric-reject-form"

							name="modify-nric-reject-form"

							form={
								form
							}

							labelCol={{
								span:
									24,
							}}

							onFinish={
								rejectRequest
							}

							disabled={
								reject.loading
							}

							validateTrigger={[
								'onBlur',
								'onChange',
							]}
						>

							<Row
								gutter={
									20
								}
							>

								<Col
									xs={
										24
									}
								>

									<Form.Item
										label="Remarks"

										name="remarks"

										rules={[
											{
												required:
													true,

												whitespace:
													true,

												message:
													'Please enter rejection remarks!',
											},
										]}
									>

										<Input.TextArea
											placeholder="Please enter reason for rejection"

											autoSize={{
												minRows:
													5,

												maxRows:
													5,
											}}
										/>

									</Form.Item>

								</Col>


								<Col
									xs={
										24
									}
								>

									<Form.Item
										style={{
											marginBottom:
												0,
										}}
									>

										<Button
											loading={
												reject.loading
											}

											type="primary"

											htmlType="submit"

											block
										>
											Reject Request
										</Button>

									</Form.Item>

								</Col>

							</Row>

						</Form>

					</Modal>
				)
			}

		</div>
	);
};

export default PendingModifyNric;